import {
  Component,
  OnInit,
  HostListener,
  ElementRef,
  TemplateRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { stripTemplateGuideKeys } from '@angular-workspace/feature';
import { HeaderComponent } from '../../header/header.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { ProviderSidebarComponent } from '../../../../provider/sidebar/provider-sidebar.component';
import { OrgAdminSidebarComponent } from '../../../../org-admin/sidebar/org-admin-sidebar.component';
import { StepperComponent, StepperStep } from '../../primeng';
import {
  downloadTanuhModelMetadataTemplate,
  validateTanuhModelMetadataFormJson,
  enrichTanuhModelMetadataFromUpload,
} from '../../../utils/tanuh-metadata-template.util';
import {
  croissantDocToFormValues,
  downloadCroissantTemplate,
  platformBlockToFormValues,
  validateCroissant,
} from '../../../utils/croissant.util';

export interface DatasetData {
  title: string;
  permission: 'Open' | 'Restricted';
}

@Component({
  selector: 'app-provider-upload-metadata',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    HeaderComponent,
    ProviderSidebarComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    StepperComponent,
  ],
  templateUrl: './upload-metadata.component.html',
  styleUrl: './upload-metadata.component.scss',
})
export class ProviderUploadMetadataComponent implements OnInit, AfterViewInit {
  get isOrgAdminRoute(): boolean {
    return this.router.url.includes('/org-admin/');
  }

  uploadMetadataForm!: FormGroup;
  currentStep = 1;
  totalSteps = 4;

  @ViewChild('step2Template', { static: true }) step2Template!: TemplateRef<any>;

  stepperSteps: StepperStep[] = [];

  get stepperStepsWithLabel(): StepperStep[] {
    const label = this.isModel() ? 'Setup your Models' : 'Setup your datasets';
    return this.stepperSteps.map((step, i) => (i === 0 ? { ...step, label } : step));
  }

  datasetData: DatasetData | null = null;
  dragActive = false;
  selectedFile: File | null = null;
  uploadError: string | null = null;
  /** Blocking problems found in the uploaded file (shown under `uploadError`). */
  missingFields: string[] = [];
  /** Advisory Croissant findings; the file can still be used. */
  uploadWarnings: string[] = [];

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {}

  private getLocalStorageKey(): string {
    return this.isModel() ? 'modelFormData' : 'datasetFormData';
  }

  private configureStepperSteps(): void {
    if (this.isModel()) {
      this.stepperSteps = [
        { key: 'step1', label: 'Setup your Models' },
        { key: 'step2', label: 'Upload metadata' },
        { key: 'step3', label: 'Preview Metadata' },
        { key: 'step4', label: 'Upload files' },
      ];
      this.totalSteps = 4;
      this.currentStep = 1;
      return;
    }

    this.stepperSteps = [
      { key: 'step1', label: 'Setup your datasets' },
      { key: 'step2', label: 'Upload metadata' },
      { key: 'step3', label: 'Upload files' },
    ];
    this.totalSteps = 3;
    this.currentStep = 1;
  }

  private getRouteBase(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/org-admin/add-model')) {
      return 'org-admin/add-model';
    }
    if (currentUrl.includes('/org-admin/add-dataset')) {
      return 'org-admin/add-dataset';
    }
    if (currentUrl.includes('/add-model/')) {
      return 'provider/add-contributions/add-model';
    }
    if (currentUrl.includes('/add-contributions/dataset')) {
      return 'provider/add-contributions/dataset';
    }
    return 'provider/add-contributions/add-dataset';
  }

  ngOnInit(): void {
    this.configureStepperSteps();

    const localStorageKey = this.getLocalStorageKey();
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state && navigation.extras.state['contributionData']) {
      this.datasetData = navigation.extras.state['contributionData'] as DatasetData;
      localStorage.setItem(localStorageKey, JSON.stringify(this.datasetData));
    } else {
      const state = window.history.state;
      if (state && state.contributionData) {
        this.datasetData = state.contributionData as DatasetData;
        localStorage.setItem(localStorageKey, JSON.stringify(this.datasetData));
      } else {
        const savedData = localStorage.getItem(localStorageKey);
        if (savedData) {
          try {
            this.datasetData = JSON.parse(savedData) as DatasetData;
          } catch (error) {
            console.error('Error parsing saved data:', error);
            localStorage.removeItem(localStorageKey);
          }
        }
      }
    }

    this.uploadMetadataForm = this.fb.group({
      jsonSchema: ['', [Validators.required]],
    });
  }

  ngAfterViewInit(): void {
    if (this.step2Template) {
      this.stepperSteps[1].content = this.step2Template;
      this.cdr.detectChanges();
    }
  }

  onStepChange(stepIndex: number): void {
    const routeBase = this.getRouteBase();
    const uploadsStepIndex = this.isModel() ? 3 : 2;
    const routes = this.isModel()
      ? [
          routeBase,
          `${routeBase}/upload-metadata`,
          `${routeBase}/preview-metadata`,
          `${routeBase}/uploads`,
        ]
      : [routeBase, `${routeBase}/upload-metadata`, `${routeBase}/uploads`];

    if (!this.isModel() && stepIndex === uploadsStepIndex && !this.hasCreatedItemId()) {
      return;
    }

    if (stepIndex >= 0 && stepIndex < routes.length) {
      this.router.navigate([routes[stepIndex]]);
    }
  }

  onNext(): void {
    if (this.uploadMetadataForm.valid) {
      this.proceed();
    }
  }

  onPrevious(): void {
    this.goBack();
  }

  goBack(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  proceed(): void {
    if (!this.uploadMetadataForm.valid) {
      return;
    }

    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    const existingFormData = existingData ? JSON.parse(existingData) : {};
    const uploaded = this.parseUploadedJson();

    if (this.isModel()) {
      const enriched = enrichTanuhModelMetadataFromUpload(uploaded, this.datasetData ?? undefined);
      const formData = {
        ...existingFormData,
        ...this.datasetData,
        ...enriched,
        jsonSchema: JSON.stringify(enriched, null, 2),
        metadataOption: 'upload',
      };
      localStorage.setItem(localStorageKey, JSON.stringify(formData));
      this.router.navigate(['../preview-metadata'], { relativeTo: this.route });
      return;
    }

    // Datasets: the file has already passed validateCroissant(). Pre-fill the Croissant builder with
    // it; the dataset is created from the builder once the user has reviewed the values.
    const formData = {
      ...existingFormData,
      ...this.datasetData,
      ...croissantDocToFormValues(uploaded),
      ...platformBlockToFormValues(uploaded),
      croissantBase: uploaded,
      croissant: uploaded,
      metadataOption: 'upload',
    };
    localStorage.setItem(localStorageKey, JSON.stringify(formData));
    this.router.navigate(['../metadata'], { relativeTo: this.route });
  }

  isModel(): boolean {
    return this.router.url.includes('/add-model/');
  }

  downloadMockJsonTemplate(): void {
    if (this.isModel()) {
      downloadTanuhModelMetadataTemplate();
      return;
    }
    downloadCroissantTemplate();
  }

  private parseUploadedJson(): Record<string, unknown> {
    const raw = this.uploadMetadataForm.get('jsonSchema')?.value;
    if (!raw) {
      return {};
    }
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return {};
    }
  }

  private enrichAndStoreUploadedJson(
    uploaded: Record<string, unknown>,
  ): Record<string, unknown> {
    return enrichTanuhModelMetadataFromUpload(uploaded, this.datasetData ?? undefined);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFileSelection(event.dataTransfer.files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelection(input.files[0]);
      input.value = '';
    }
  }

  private handleFileSelection(file: File): void {
    this.uploadError = null;
    this.missingFields = [];
    this.uploadWarnings = [];

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== '.json') {
      this.uploadError = 'Please upload a valid JSON file.';
      this.cdr.detectChanges();
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError = 'File size exceeds 10MB. Please upload a smaller file.';
      this.cdr.detectChanges();
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const content = e.target?.result;
        if (typeof content !== 'string') {
          throw new Error('Unable to read file contents.');
        }

        const parsed = JSON.parse(content) as Record<string, unknown>;

        if (!this.isModel()) {
          const croissant = validateCroissant(parsed);
          this.uploadWarnings = croissant.warnings;
          if (croissant.errors.length > 0) {
            this.resetUploadSelection();
            this.uploadError = 'The uploaded file is not a valid Croissant document:';
            this.missingFields = croissant.errors;
            this.cdr.detectChanges();
            return;
          }
          this.uploadError = null;
          this.missingFields = [];
          this.selectedFile = file;
          this.uploadMetadataForm.patchValue({ jsonSchema: JSON.stringify(parsed, null, 2) });
          this.uploadMetadataForm.get('jsonSchema')?.markAsTouched();
          this.cdr.detectChanges();
          return;
        }

        const uploaded = stripTemplateGuideKeys(parsed);
        const modelValidation = validateTanuhModelMetadataFormJson(uploaded);
        if (modelValidation.error) {
          this.resetUploadSelection();
          this.uploadError = modelValidation.error;
          this.cdr.detectChanges();
          return;
        }
        if (!modelValidation.valid) {
          this.resetUploadSelection();
          this.uploadError = 'The uploaded JSON is missing the following mandatory field(s):';
          this.missingFields = modelValidation.missingFields;
          this.cdr.detectChanges();
          return;
        }

        const enriched = this.enrichAndStoreUploadedJson(uploaded);
        this.uploadError = null;
        this.missingFields = [];
        this.selectedFile = file;
        this.uploadMetadataForm.patchValue({
          jsonSchema: JSON.stringify(enriched, null, 2),
        });
        this.uploadMetadataForm.get('jsonSchema')?.markAsTouched();
        this.cdr.detectChanges();
      } catch {
        this.uploadError = 'Invalid JSON file. Please ensure the file contains valid JSON.';
        this.resetUploadSelection();
      }
    };
    reader.onerror = () => {
      this.uploadError = 'Error reading file. Please try again.';
      this.resetUploadSelection();
    };
    reader.readAsText(file);
  }

  private resetUploadSelection(): void {
    this.selectedFile = null;
    this.missingFields = [];
    this.uploadWarnings = [];
    this.uploadMetadataForm.patchValue({ jsonSchema: '' });
    this.cdr.detectChanges();
  }

  removeFile(): void {
    this.selectedFile = null;
    this.uploadError = null;
    this.missingFields = [];
    this.uploadWarnings = [];
    this.uploadMetadataForm.patchValue({ jsonSchema: '' });
    this.uploadMetadataForm.get('jsonSchema')?.markAsUntouched();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement.click();
  }

  isNextButtonDisabled(): boolean {
    return !this.selectedFile || !this.uploadMetadataForm.get('jsonSchema')?.value;
  }

  private hasCreatedItemId(): boolean {
    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    if (!existingData) {
      return false;
    }
    try {
      const formData = JSON.parse(existingData) as Record<string, unknown>;
      return Boolean(formData['itemId'] || formData['id'] || formData['item_id']);
    } catch {
      return false;
    }
  }
}
